/**
 * PATH: apps/quote/src/lib/overlay/quoteOverlay.ts
 * PURPOSE: Tenant overlay seam for the canonical quote hub (Track 2.1a-ii).
 * WHY: Per QUOTE_BUILDER_CANONICAL_DECISION.md §5.2 + CONSOLIDATION_OVERLAY_SPEC.md,
 *      the canonical engine must take tenant deltas (branding tokens +
 *      rate-profile defaults) through a SEAM — never inlined constants or
 *      `tenant === ...` branches. This mirrors the blessed in-repo runtime
 *      pattern (settings-hub `setSettingsHubRuntime`, tenant-shared
 *      `apiAuthSeam`/`commandPaletteSeam`): a module-scoped singleton with
 *      DEFAULTS + a merge setter + a non-hook getter.
 *
 *      DEFAULTS are byte-equal to the values previously inlined in
 *      `config/navigation.ts` (branding) and `pages/SettingsPage.tsx`
 *      (rate-profile), so a host that registers nothing keeps today's
 *      behaviour exactly. Tenant migrations (Track 2.2) call
 *      `setQuoteOverlay(...)` once at boot, before first render.
 *
 * FLOW:
 *   setQuoteOverlay(partial)  ->  module singleton  ->  getQuoteOverlay()
 *
 * SCOPE NOTE: the pure pricing engine (`lib/quote-builder-math/computeLine.ts`,
 *   `HOURS_PER_DAY`) is intentionally NOT wired to this seam yet — consuming a
 *   tenant rate-profile inside the math engine changes live pricing and is
 *   gated behind a golden-master/visual-regression MR (Track 2.1a-iii). The
 *   `rateProfile.hoursPerDay` default here mirrors that constant so the
 *   contract is complete and ready for that step.
 */

export interface QuoteBrandingOverlay {
  /** Tailwind color name for the hub accent (e.g. 'amber'). */
  accentColor: string
  /** Tailwind gradient classes for the hub accent. */
  accentGradient: string
}

export interface QuoteRateProfile {
  /** Working hours per day for day<->hours conversions. Mirrors computeLine HOURS_PER_DAY. */
  hoursPerDay: number
  defaultMarginPercent: number
  defaultGstRate: number
  defaultValidityDays: number
  defaultMarkupPercent: number
}

export interface QuoteOverlay {
  branding: QuoteBrandingOverlay
  rateProfile: QuoteRateProfile
}

export interface QuoteOverlayPartial {
  branding?: Partial<QuoteBrandingOverlay>
  rateProfile?: Partial<QuoteRateProfile>
}

const DEFAULTS: QuoteOverlay = {
  branding: {
    accentColor: 'amber',
    accentGradient: 'from-amber-500 to-orange-600',
  },
  rateProfile: {
    hoursPerDay: 8,
    defaultMarginPercent: 35,
    defaultGstRate: 10,
    defaultValidityDays: 30,
    defaultMarkupPercent: 25,
  },
}

function cloneOverlay(o: QuoteOverlay): QuoteOverlay {
  return { branding: { ...o.branding }, rateProfile: { ...o.rateProfile } }
}

let _overlay: QuoteOverlay = cloneOverlay(DEFAULTS)

/** Register tenant overlay deltas at boot (before first render). Merges per sub-object. */
export function setQuoteOverlay(partial: QuoteOverlayPartial): QuoteOverlay {
  _overlay = {
    branding: { ..._overlay.branding, ...(partial.branding ?? {}) },
    rateProfile: { ..._overlay.rateProfile, ...(partial.rateProfile ?? {}) },
  }
  return _overlay
}

/** Read the resolved overlay (DEFAULTS when nothing registered). Not a hook. */
export function getQuoteOverlay(): QuoteOverlay {
  return _overlay
}

/** Test isolation only — restores DEFAULTS. */
export function _resetQuoteOverlayForTesting(): void {
  _overlay = cloneOverlay(DEFAULTS)
}
